import { formatFileSize, getLocaleDateString } from 'utils'
import { useGetQuotingProspectsProspectIdCensusId } from 'utils/web_api'
import { type QuotingEmployeeCensus } from 'utils/web_api.schemas'

import { BeingProcessedBanner, CensusParsingError } from './CensusBanners'
import { GettingCensusFailed } from './GetErrorBanners'
import { ProspectEmployeeList } from './ProspectEmployeeList'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import Badge from '../reusable/Badge'

export const getTypeForCensusStatus = (status: string) => {
  switch (status) {
    case 'unprocessed':
      return 'default'
    case 'processing':
      return 'attention'
    case 'processed':
      return 'success'
    case 'failed':
      return 'critical'
    default:
      return 'default'
  }
}

export function CensusDetailsBox ({
  census,
}: {
  census: QuotingEmployeeCensus;
}) {
  return (
    <div className='flex gap-x-10'>
      <dl className='flex grow flex-col gap-y-4 rounded-lg border border-ds-border-primary-default p-4 text-sm text-ds-text-default'>
        <div className='flex gap-x-6 items-center'>
          <dt className='font-medium w-1/3'>File name</dt>
          <dd className='flex-1'>
            {census.file_url
              ? (
                <a href={census.file_url} target='_blank' rel='noopener noreferrer'>
                  {census.file_name}
                </a>
                )
              : (
                  census.file_name
                )}
            {census.file_size && ` (${formatFileSize(census.file_size)})`}
          </dd>
        </div>
        <div className='flex gap-x-6 items-center'>
          <dt className='font-medium w-1/3'>File format</dt>
          <dd className='flex-1'>
            {census.format === 'generic' ? 'Default' : 'Rippling'}
          </dd>
        </div>
        <div className='flex gap-x-6 items-center'>
          <dt className='font-medium w-1/3'>Parsing status</dt>
          <dd className='flex-1'>
            <Badge
              type={getTypeForCensusStatus(census.status)}
              text={census.status}
            />
          </dd>
        </div>
        <div className='flex gap-x-6 items-center'>
          <dt className='font-medium w-1/3'>Date created</dt>
          <dd className='flex-1'>
            {getLocaleDateString(new Date(census.created_at))}
          </dd>
        </div>
        <div className='flex gap-x-6 items-center'>
          <dt className='font-medium w-1/3'>Date updated</dt>
          <dd className='flex-1'>
            {getLocaleDateString(new Date(census.updated_at))}
          </dd>
        </div>
      </dl>
      {census.parsing_errors && census.parsing_errors.length > 0 && (
        <CensusParsingError errors={census.parsing_errors} />
      )}
    </div>
  )
}

function CensusDetailsImpl ({
  census,
  prospectId,
}: {
  census: QuotingEmployeeCensus;
  prospectId: string;
}) {
  return (
    <div className='flex flex-col gap-y-4'>
      {census.status === 'processing'
        ? (
          <BeingProcessedBanner />
          )
        : (
          <>
            <CensusDetailsBox census={census} />
          </>
          )}
      {census.status === 'processed' && (
        <ProspectEmployeeList prospectId={prospectId} censusId={census.id} />
      )}
    </div>
  )
}

function CensusDetailWithFetching ({
  censusId,
  prospectId,
}: {
  censusId: string;
  prospectId: string;
}) {
  const { data, error, isLoading } = useGetQuotingProspectsProspectIdCensusId(
    prospectId,
    censusId,
    {
      query: {
        enabled: !!censusId,
        retry: false,
        refetchInterval: (query) => {
          if (['processed', 'failed'].includes(query.state.data?.status ?? '')) {
            return false
          }
          return 5000
        },
      }
    }
  )

  // TODO: improve the loading state and empty census state
  if (isLoading && !data) {
    return <div>Loading...</div>
  }
  if (!isLoading && error && !data) {
    return <GettingCensusFailed errorObj={error as GrapeAPIErrorType} />
  }
  if (!isLoading && !error && !data) {
    return <div>Census not found</div>
  }
  return (
    <>
      <h1 className='heading-lg pb-10'>Census details</h1>
      <div className='flex flex-col gap-y-4'>
        {data && (
          <CensusDetailsImpl
            census={data}
            prospectId={prospectId}
          />
        )}
      </div>
    </>
  )
}

/**
 * This component is used to show the details of census upload while the quote is being created
 * (launched from the quote details page & during create quote flow)
 * This is also used to render the census details
 * from the census list section of the prospect details page
 */
export default function ProspectCensusDetails ({
  census,
  censusId,
  prospectId,
}: {
  census: QuotingEmployeeCensus;
  censusId: string;
  prospectId: string;
}) {
  // If census is already available, that means this component is being used for quote details page
  // or create quote flow. If census is not available, that means this component is being used
  // from the census list page or via the page URL directly. In that case, we need to fetch
  // the census via the API, and we would need to set up QueryClientProvider to do that.
  // (Given that this component would be come the 'top level component')
  return census
    ? (
      <CensusDetailsImpl
        census={census}
        prospectId={prospectId}
      />
      )
    : (
      <CensusDetailWithFetching
        censusId={censusId}
        prospectId={prospectId}
      />
      )
}
